import { createTheme } from "@mui/material/styles";
import palette from "./palette";
import typography from "./typography";

declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        "outlined-secondary": true;
    }
}

declare module "@mui/material/Paper" {
    interface PaperPropsVariantOverrides {
        "list-wrapper": true;
    }
}

declare module "@mui/material/styles/createPalette" {
    interface PaletteColor extends ColorPartial {
        0: string | undefined;
        4: string | undefined;
        5: string | undefined;
        blue: {
            500: string;
        };
        navBarColors: {
            button_bg_clr: string;
            button_border_clr: string;
            button_selected_bg_clr: string;
        };
    }
    interface TypeText extends ColorPartial {
        10: string | undefined;
    }
}

declare module "@mui/material/styles/createTypography" {
    interface Typography {
        fontWeightSemibold: React.CSSProperties["fontWeight"];
    }
}

const theme = createTheme({
    typography: typography,
    palette: palette,
    components: {
        MuiTableContainer: {
            styleOverrides: {
                // No CssBaseline: content-box + width:100% + border overflows the parent by 2px.
                root: { boxSizing: "border-box" },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "4px 16px",
                    height: "40px",
                    fontSize: "15px",
                    fontWeight: theme.typography.fontWeightSemibold,
                    lineHeight: "24px",
                }),
            },
            variants: [
                {
                    props: { variant: "outlined-secondary" },
                    style: {
                        padding: "4px 24px",
                        border: "1px solid #BFBFBF",
                        borderRadius: "4px",
                        fontWeight: 500,
                    },
                },
            ],
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: "8px",
                    padding: "12px 16px",
                    ".MuiListItemIcon-root": {
                        minWidth: "32px",
                        ".MuiSvgIcon-root": {
                            fill: "black",
                        },
                    },
                    "&.Mui-selected": {
                        backgroundColor: theme.palette.primary[50],
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary[200]}`,
                        ".MuiListItemIcon-root": {
                            ".MuiSvgIcon-root": {
                                fill: theme.palette.primary.main,
                            },
                        },
                    },
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    backgroundColor: "#fafafa",
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    wordBreak: "break-all",
                },
                head: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text[10],
                }),
            },
            variants: [
                {
                    props: { size: "small" },
                    style: {
                        height: "40px",
                    },
                },
            ],
        },
        MuiPaper: {
            variants: [
                {
                    props: { variant: "list-wrapper" },
                    style: {
                        width: "100%",
                        boxShadow: "0px 0px 8px 0px #00000014",
                        borderRadius: "8px",
                        overflow: "hidden",
                    },
                },
            ],
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: "36px",
                    padding: "11px 16px",
                    "&.Mui-selected, &.Mui-selected:hover": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    },
                }),
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: ({ theme }) => ({
                    p: {
                        color: theme.palette.text.primary,
                    },
                    button: {
                        color: theme.palette.primary[700],
                    },
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: "8px",
                    padding: "2px 16px",
                },
            },
            variants: [
                {
                    props: { color: "primary" },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.primary[50],
                        color: theme.palette.text[900],
                    }),
                },
                {
                    props: { size: "small" },
                    style: {
                        fontSize: "14px",
                        lineHeight: "22px",
                        height: "26px",
                    },
                },
            ],
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    ".MuiTabs-indicator": {
                        left: 0,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: "8px",
                    minHeight: "auto",
                    width: "100%",
                    alignItems: "flex-start",
                    color: theme.palette.text[10],
                    "&.Mui-selected": {
                        color: theme.palette.text[10],
                        backgroundColor: theme.palette.primary[50],
                    },
                }),
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "& .MuiSwitch-switchBase.Mui-checked": {
                        color: theme.palette.primary.blue[500],
                        "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.08)" },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: theme.palette.primary.blue[500],
                    },
                }),
            },
        },
    },
});

export default theme;
